import { visibleGuardrailChecks } from "./GuardrailCheckList";
import type {
  ApprovalStep,
  GuardrailCheck,
  GuardrailDecision,
} from "./RequisitionGuardrailOutcome";

const byOrder = (steps: readonly ApprovalStep[]): ApprovalStep[] =>
  [...steps].sort((left, right) => left.order - right.order);

const effectOf = (check: GuardrailCheck) => check.effect ?? "BLOCK";

const counted = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

const groupsOf = (step: ApprovalStep) =>
  step.kind === "group" ? (step.groups ?? []) : [];

/** A refused group is still the gate. Only a full APPROVED step is done. */
const fullyApproved = (step: ApprovalStep): boolean => {
  const groups = groupsOf(step);
  return (
    groups.length > 0 && groups.every((group) => group.status === "APPROVED")
  );
};

const evaluationStep = (order: number): ApprovalStep => ({
  order,
  kind: "evaluation",
});

const withEvaluation = (steps: ApprovalStep[]): ApprovalStep[] => {
  if (steps.some((step) => step.kind === "evaluation")) {
    return steps;
  }
  const first = steps[0];
  return [evaluationStep(first ? first.order - 1 : 0), ...steps];
};

const isAttention = (check: GuardrailCheck): boolean => {
  if (check.state === "PASS") {
    return false;
  }
  const effect = effectOf(check);
  return effect === "WARN" || effect === "BLOCK";
};

/** Failures the card keeps on screen. Routing misses are already gone. */
export const attentionGuardrailChecks = (
  checks: readonly GuardrailCheck[]
): GuardrailCheck[] => visibleGuardrailChecks([...checks]).filter(isAttention);

/** Ticks the evaluation summary collapses. */
export const summaryGuardrailChecks = (
  checks: readonly GuardrailCheck[]
): GuardrailCheck[] =>
  visibleGuardrailChecks([...checks]).filter((check) => !isAttention(check));

export const evalCollapsedTitle = (
  checks: readonly GuardrailCheck[]
): string => {
  const shown = summaryGuardrailChecks(checks);
  if (shown.length === 0) {
    return "Automatic evaluation";
  }
  return `Automatic evaluation · ${counted(shown.length, "rule passed", "rules passed")}`;
};

export const quietGroupLine = (group: {
  approverGroupName?: string | null;
  status?: string | null;
  personName?: string | null;
}): string => {
  const name = group.approverGroupName?.trim() || "Approver group";
  if (group.status === "APPROVED") {
    const who = group.personName?.trim();
    return who ? `${name} · Approved · ${who}` : `${name} · Approved`;
  }
  if (group.status === "REFUSED") {
    const who = group.personName?.trim();
    return who ? `${name} · Refused · ${who}` : `${name} · Refused`;
  }
  return `${name} · Waiting`;
};

/**
 * Rows to render. A missing evaluation is synthetic. HARD_BLOCK is that row
 * only — the hook's steps used for writes are left alone.
 */
/** A rule can strip every group off a step. That step is not part of the chain. */
const keptStep = (step: ApprovalStep): boolean =>
  step.kind !== "group" || (step.groups?.length ?? 0) > 0;

export const displaySteps = (
  decision: GuardrailDecision,
  steps: ApprovalStep[] | null | undefined
): ApprovalStep[] => {
  const withEval = withEvaluation(byOrder(steps ?? [])).filter(keptStep);
  if (decision !== "HARD_BLOCK") {
    return withEval;
  }
  const evaluation = withEval.find((step) => step.kind === "evaluation");
  return evaluation ? [evaluation] : [evaluationStep(0)];
};

/** Eval is current only on a hard block, where it is the only row. */
export const currentStepOrder = (
  decision: GuardrailDecision,
  steps: readonly ApprovalStep[]
): number | null => {
  if (decision === "HARD_BLOCK") {
    return steps.find((step) => step.kind === "evaluation")?.order ?? null;
  }
  const reason = steps.find(
    (step) => step.kind === "reason" && step.status === "PENDING"
  );
  if (reason) {
    return reason.order;
  }
  const group = steps.find(
    (step) => step.kind === "group" && !fullyApproved(step)
  );
  return group?.order ?? null;
};

/** A PASS stamp with a human step still open. Hard blocks are not waiting. */
export const awaitingApproval = (result: {
  guardrailDecision?: GuardrailDecision | null;
  guardrailEvaluation?: { steps?: ApprovalStep[] | null } | null;
}): boolean => {
  if (result.guardrailDecision !== "PASS") {
    return false;
  }
  const steps = displaySteps("PASS", result.guardrailEvaluation?.steps);
  const order = currentStepOrder("PASS", steps);
  const current = steps.find((step) => step.order === order);
  // A pending exception reason is the requestor's, not an approver's.
  if (current == null || current.kind !== "group") {
    return false;
  }
  return !(current.groups ?? []).some((group) => group.status === "REFUSED");
};

/**
 * Complete PASS chain with at least one group. Shown for a draft (or a result
 * that has no lifecycle state yet). Hidden once the requisition has moved on.
 */
export const chainMayOpen = ({
  decision,
  steps,
  currentOrder,
  state,
}: {
  decision: GuardrailDecision;
  steps: readonly ApprovalStep[];
  currentOrder: number | null;
  state?: string | null | undefined;
}): boolean => {
  if (decision !== "PASS" || currentOrder != null) {
    return false;
  }
  if (!steps.some((step) => step.kind === "group")) {
    return false;
  }
  return state == null || state === "DRAFT";
};
